import { SettingsService } from "@/services/admin-services";
import { SettingsFooterLink } from "@/services/admin-services/models";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { FormEvent, useState } from "react";

interface LinkDialogProps {
    link: SettingsFooterLink;
    onClose: () => void;
    onSubmit: (link: SettingsFooterLink) => void;
}

const LinkDialog = ({ link, onClose, onSubmit }: LinkDialogProps) => {
    const [text, setText] = useState<string>(link.linkText);
    const [target, setTarget] = useState<string>(link.targetUrl);
    const [order, setOrder] = useState<number>(link.order);

    const [isSaving, setIsSaving] = useState<boolean>(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const settingsService = new SettingsService("int");

    const onFormSubmit = (event: FormEvent) => {
        event.preventDefault();

        if (!text || !target || isNaN(order)) return;

        setIsSaving(true);

        const body = { ...link, linkText: text, targetUrl: target, order: order };

        let promise: Promise<SettingsFooterLink>;
        if (link.id) promise = settingsService.updateFooterLink(link.id, body);
        else promise = settingsService.createFooterLink(body);

        promise.then((result) => {
            setIsSaving(false);
            onSubmit(result || body);
        });
    };

    return (
        <Dialog open={Boolean(link)} onClose={onClose} fullWidth maxWidth={"sm"} fullScreen={fullScreen}>
            <form onSubmit={(event) => onFormSubmit(event)}>
                <DialogTitle>{link.id ? "Edit Existing Link" : "Add New Link"}</DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        pointerEvents: isSaving ? "none" : "auto",
                    }}
                >
                    <TextField
                        autoFocus
                        fullWidth
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                        label={`Text`}
                        type="text"
                        variant="standard"
                        sx={{ mb: 3 }}
                        disabled={isSaving}
                    />
                    <TextField
                        autoFocus
                        fullWidth
                        value={target}
                        onChange={(event) => setTarget(event.target.value)}
                        label={`URL`}
                        type="text"
                        variant="standard"
                        sx={{ mb: 3 }}
                        disabled={isSaving}
                    />
                    <TextField
                        autoFocus
                        fullWidth
                        value={order}
                        onChange={(event) => setOrder(parseInt(event.target.value))}
                        label={`Order`}
                        type="number"
                        variant="standard"
                        sx={{ mb: 3 }}
                        disabled={isSaving}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="contained" type="submit" disabled={isSaving}>
                        {link.id ? "Edit" : "Add"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default LinkDialog;
